


































































































































































import { Component, Provide, Prop, Watch, Vue } from 'vue-property-decorator';

@Component
export default class ChartRawWalkingDetailed extends Vue {
  @Prop({ default: null, required: true }) values: any;
  @Prop({ required: true }) degraded!: boolean;

  @Provide() walkingPhase: boolean = true;
  @Provide() oscillatingPhase: boolean = false;
  @Provide() stancePhase: boolean = false;

  @Provide() tableData: any = {
    walkingPhase: [],
    oscillatingPhase: [],
    stancePhase: [],
  };

  get walkingPhaseData() {
    return [
      {
        id: 'stance_time',
        name: this.$t('commons.standards.contact-time'),
        desc: this.$t('commons.sentences.tooltips.contact-time'),
        unit: 'ms',
        showData: true,
        conversion: 'secToMs',
      },
      {
        id: 'swing_time',
        name: this.$t('commons.standards.flying-time'),
        desc: this.$t('commons.sentences.tooltips.flight-time'),
        unit: 'ms',
        showData: true,
        conversion: 'secToMs',
      },
      {
        id: 'loading_time',
        name: this.$t('commons.standards.taligrade'),
        desc: null,
        unit: 'ms',
        showData: false,
        conversion: 'secToMs',
      },
      {
        id: 'flatfoot_time',
        name: this.$t('commons.standards.plantigrade'),
        desc: null,
        unit: 'ms',
        showData: false,
        conversion: 'secToMs',
      },
      {
        id: 'propulsion_time',
        name: this.$t('commons.standards.digitigrade'),
        desc: null,
        unit: 'ms',
        showData: false,
        conversion: 'secToMs',
      },
    ];
  }
  get oscillatingPhaseData() {
    return [
      {
        id: 'swing_speed',
        name: this.$t('commons.standards.average-speed-of-stride'),
        desc: null,
        unit: 'm/s',
        showData: true,
      },
      {
        id: 'propulsion_speed',
        name: this.$t('commons.standards.propulsion-speed'),
        desc: null,
        unit: 'm/s',
        showData: true,
      },
      {
        id: 'walking_speed',
        name: this.$t('commons.standards.walking-speed'),
        desc: this.$t('commons.sentences.tooltips.speed'),
        unit: 'km/h',
        showData: true,
        conversion: 'mpsToKmh',
        degraded: true,
      },
      {
        id: 'stride_length',
        name: this.$t('commons.standards.stride-length'),
        desc: this.$t('commons.sentences.tooltips.stride-length'),
        unit: 'm',
        showData: true,
        degraded: true,
      },
      {
        id: 'step_by_step_cadence',
        name: this.$t('commons.standards.cadence'),
        desc: this.$t('commons.sentences.tooltips.cadence'),
        unit: this.$t('commons.units.steps-min'),
        showData: true,
        degraded: true,
      },
    ];
  }
  get stancePhaseData() {
    return [
      {
        id: 'pronation_angle_foot_in',
        name: this.$t('commons.standards.heel-strike'),
        desc: null,
        unit: '°',
        showData: true,
      },
      {
        id: 'pronation_angle_FFO',
        name: this.$t('commons.standards.toe-strike'),
        desc: null,
        unit: '°',
        showData: true,
      },
      {
        id: 'pronation_angle_FFI',
        name: this.$t('commons.standards.heel-off'),
        desc: null,
        unit: '°',
        showData: true,
      },
      {
        id: 'pronation_angle_foot_out',
        name: this.$t('commons.standards.toe-off'),
        desc: null,
        unit: '°',
        showData: true,
      },
    ];
  }

  @Watch('values', { deep: true })
  onValuesChange() {
    this.initValues();
  }

  initValues() {
    this.tableData = {
      walkingPhase: [],
      oscillatingPhase: [],
      stancePhase: [],
    };

    if (!this.degraded) {
      for (const value of this.walkingPhaseData) {
        this.tableData.walkingPhase.push({
          ...value,
          symmetry: this.getSymmetry(value.id, false, value.conversion),
          min: this.getMinValue(value.id, 0, value.conversion),
          max: this.getMaxValue(value.id, 0, value.conversion),
        });
      }
      for (const value of this.stancePhaseData) {
        this.tableData.stancePhase.push({
          ...value,
          symmetry: this.getSymmetry(value.id, true),
          min: this.getMinValue(value.id, 1),
          max: this.getMaxValue(value.id, 1),
        });
      }
    } else {
      this.walkingPhase = false;
      this.oscillatingPhase = true;
    }

    const oscillatingData = this.degraded
      ? this.oscillatingPhaseData.filter((x: any) => x.degraded)
      : this.oscillatingPhaseData;
    for (const value of oscillatingData) {
      this.tableData.oscillatingPhase.push({
        ...value,
        symmetry: this.getSymmetry(value.id, false, value.conversion),
        min: this.getMinValue(value.id, 1, value.conversion),
        max: this.getMaxValue(value.id, 1, value.conversion),
      });
    }
  }

  getSymmetry(id: string, angleType: boolean, conversion?: string) {
    const left = this.valueConversion(this.values.left.aggregates[id].avg, conversion);
    const right = this.valueConversion(this.values.right.aggregates[id].avg, conversion);
    if (angleType) {
      const rsl = Math.abs(left - right);
      return parseFloat(rsl.toFixed(1));
    } else {
      const rsl = (1 - Math.abs(right - left) / Math.max(right, left)) * 100;
      return parseFloat(rsl.toFixed(1));
    }
  }
  getMinValue(id: string, decimal: number, conversion?: string) {
    const left = this.values.left.aggregates[id];
    const right = this.values.right.aggregates[id];

    const leftRsl = this.valueConversion(left.avg, conversion) - this.valueConversion(left.std, conversion);
    const rightRsl = this.valueConversion(right.avg, conversion) - this.valueConversion(right.std, conversion);

    return {
      left: parseFloat(leftRsl.toFixed(decimal)),
      right: parseFloat(rightRsl.toFixed(decimal)),
    };
  }
  getMaxValue(id: string, decimal: number, conversion?: string) {
    const left = this.values.left.aggregates[id];
    const right = this.values.right.aggregates[id];

    const leftRsl = this.valueConversion(left.avg, conversion) + this.valueConversion(left.std, conversion);
    const rightRsl = this.valueConversion(right.avg, conversion) + this.valueConversion(right.std, conversion);

    return {
      left: parseFloat(leftRsl.toFixed(decimal)),
      right: parseFloat(rightRsl.toFixed(decimal)),
    };
  }

  valueConversion(value: number, conversion?: string) {
    if (conversion) {
      switch (conversion) {
        case 'secToMs':
          return this.$pdc.helper!.secToMs(value);
        case 'mpsToKmh':
          return this.$pdc.helper!.mpsToKmh(value);
        default:
          return value;
      }
    } else {
      return value;
    }
  }

  mounted() {
    this.initValues();
  }
}
